/**
 * client/src/pages/Wait.tsx — §3.12 and §3.14. **This is the page that will actually run.**
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * spark-1 IS DOWN, SO THE DEFERRED PATH IS THE PATH
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * "Ground truth: spark-1 is down right now, exits 28 on both `:8095` and `:8006`, has no systemd
 * unit **on purpose**, is recovered by a human typing `kuda scene activate 115`, shares one GPU with
 * a 40 GB vLLM, an ASR and an embedder, and is stopped by a watchdog when `MemAvailable` falls.
 * Under those conditions, 'the GPU is unavailable at the end of a twelve-minute session' is not an
 * edge case. **It is a scheduled event.**"
 *
 * So this file is not a loading screen with a fallback. It is a screen with two designed endings,
 * and today it takes the second one every time. Both endings are warm, both are honest, and neither
 * of them is an error — `<Notice>`'s default skin is `--deferred`, which is the lifted palette's
 * reserved "in flight" colour, precisely so that "we'll have it for you this evening" cannot be
 * painted in caution-amber by accident.
 *
 * **What she must never learn is that her voice was not good enough, or that she broke it. The
 * embarrassment is the churn mechanism, and there is no re-engagement email for your mother.**
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE THREE THINGS §3.12 DEMANDS, AND WHERE EACH ONE IS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *  1. **An honest count of what is saved**, small and quiet — `FAMILY.progress.saved`. It is drawn
 *     only while the drain is behind, and it disappears when it catches up rather than sitting there
 *     saying "5 of 5" like a certificate.
 *  2. **A stated budget that counts down in seconds** — `REVEAL_BUDGET_MS` (75 s, p90, §3.12's
 *     derivation: 2 s seed assembly + 20 s `POST /voices` + 30 s generation and admission-queue wait
 *     + 5 s transfer). It counts *seconds*, never a percentage and never a bar: "not a spinner, and
 *     not a progress bar that lies."
 *  3. **Something of her own to look at** — her waveforms and the things she said.
 *
 * ⚠️ **And the countdown is not drawn at all when we already know there will be no audio.** If
 * `ttsConfigured` is false or the stored `/health` says `modelLoaded: false`, a 75-second countdown
 * is a lie with a clock on it. The page goes straight to the deferred ending. This is the single
 * most important conditional in the file: §3.14's whole argument is that *discovering* the outage at
 * the end is the failure and *choosing* it at the start is not, and a countdown that runs out into a
 * deferral converts the choice she was offered back into the discovery.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THIS IS THE RE-ENTRANT WAIT, NOT THE STUDIO'S OWN
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `client/src/studio/Studio.tsx` owns the *live* waiting step, inside its own mount, and it must:
 * a route change would unmount the `AudioContext` and the encode worker (see `App.tsx`'s header). So
 * this page is not that screen, and it is not a duplicate of it. It is the version that works when
 * the tab is gone.
 *
 * It is routed at **`/s/:sessionId/wait`** and it is reached three ways, all of them after the fact:
 *
 *   · the laptop slept, the tab restored, and somebody pressed reload on the screen holding the only
 *     durable handle to a recording of their mother;
 *   · `/back/:claimToken` resolved a session that has not been revealed yet — today, every session;
 *   · `<Reveal>` got a 404 for the payoff and sent her here, because this page owns the honest
 *     ending, the claim link and the retention promise.
 *
 * Two consequences follow and both are load-bearing:
 *
 *   · **It re-runs `POST /complete`, and that call must therefore be idempotent on the server.** It
 *     is guarded here with a ref against StrictMode's double invoke, but a page that can be opened
 *     from a link cannot promise the server it will only ever be opened once. A `complete` that
 *     enqueues a second clone job per reload would put two jobs on the one serialized GPU for one
 *     woman's session, on a box shared with audiobook's narration queue.
 *   · **It assumes nothing was handed to it.** `peaks`, `claimToken` and `health` arrive in router
 *     state when the studio navigated here, and are simply absent when a link did. Everything the
 *     page needs is fetched: `GET /sessions/:id` for the state and the plan, `GET /sessions/:id/links`
 *     for the durable handle. When the waveforms are absent they are not drawn — no placeholder, no
 *     grey box pretending to be a drawing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { HealthReport, SessionView } from "../../../shared/protocol.ts";
import { FAMILY } from "../../../shared/voice/copy.ts";
import { REVEAL_BUDGET_MS } from "../../../shared/voice/policy.ts";
import { promptById } from "../../../shared/voice/script.ts";
import { ScriptError } from "../../../shared/voice/script.ts";
import type { SessionLinks } from "../../../shared/protocol.ts";
import { api, describeForConsole } from "../lib/api.ts";
import { emailClaimLink } from "../lib/claimLink.ts";
import { useSessionHandle } from "../lib/handle.ts";
import { say } from "../lib/say.ts";
import { useSession } from "../lib/session.tsx";
import { Announce, Blank, Button, CopyButton, Field, Notice, Stage, Waveform } from "../ui/primitives.tsx";

/**
 * How often to ask whether the payoff is ready. 2 s.
 *
 * Derived rather than picked: the budget is 75 s p90 and the countdown is drawn in whole seconds, so
 * a poll slower than the countdown's own resolution would let the clock reach zero several seconds
 * before the page noticed the audio had arrived. 2 s is fast enough that the gap is invisible and
 * slow enough that a 75-second wait is 37 requests rather than 750 — on the app process that is also
 * draining her uploads to the origin at the same time.
 */
const POLL_MS = 2000;

/**
 * When to stop believing in the payoff and take the deferred ending. 3 × the budget.
 *
 * The budget is a **p90**, so overrunning it is normal and is not evidence of anything; §5.6 records
 * spark-1 as fully serialized at about one chunk per 4.1 s and shared with a 40 GB vLLM, so a queue
 * behind another job legitimately blows through 75 s. Three times it is a different claim: at 225 s
 * the admission queue has not moved at all, which on that box means the watchdog took Chatterbox
 * down when `MemAvailable` fell — the scheduled event. Deferring then is correct and is exactly what
 * §3.14 designed the ending for.
 */
const GIVE_UP_MS = REVEAL_BUDGET_MS * 3;

type Ending = "waiting" | "deferred";

interface HandedOver {
  peaks?: number[][];
  claimToken?: string;
  health?: HealthReport | null;
}

export function Wait() {
  /**
   * ⚠️ **`:sessionId` is a `ses_` id when the studio navigated here and a `vox_` CLAIM TOKEN when a
   * link did** — `/back/:claimToken` sends both here and to `<Reveal>` unresolved, and §2.4's whole
   * promise is that the link keeps working on a browser that has never seen this site.
   *
   * The raw segment is therefore not a session id and must not be used as one: the server refuses
   * anything that is not a `ses_` id before it queries. `useSessionHandle` resolves it, and every call
   * below uses `sessionId` — the resolved id — with `claimToken` presented alongside, because the
   * holder is a guest who is not this session's actor.
   */
  const { sessionId: handle = "" } = useParams();
  const navigate = useNavigate();
  const { app } = useSession();
  const handed = (useLocation().state ?? {}) as HandedOver;
  const { session: resolved, sessionId, claimToken, notFound } = useSessionHandle(handle, "wait");

  const [session, setSession] = useState<SessionView | null>(null);
  const [links, setLinks] = useState<SessionLinks | null>(null);
  const [ending, setEnding] = useState<Ending>("waiting");
  const [leftMs, setLeftMs] = useState(REVEAL_BUDGET_MS);
  const [emailed, setEmailed] = useState(false);
  const [email, setEmail] = useState("");

  const tokenRef = useRef<HTMLParagraphElement | null>(null);

  /**
   * Seed the view from the resolution rather than waiting for the first poll.
   *
   * `useSessionHandle` has already fetched a `SessionView` to find the id; throwing it away would mean
   * `POST /complete` (which can take seconds) runs before there is a name, a plan or a saved count on
   * the screen — a blank page at the exact moment §3.12 says she must be given something of her own to
   * look at. `prev ?? resolved` so a live poll is never overwritten by the stale first read.
   */
  useEffect(() => {
    if (resolved) setSession((prev) => prev ?? resolved);
  }, [resolved]);

  /**
   * Known before the first poll: there is no GPU, so there will be no audio.
   *
   * Two independent sources, and either one is enough. `ttsConfigured` is the deployment's setting
   * (`VOX_TTS_MODE=none`, which is the correct setting today and a supported product state, not a
   * degraded one). `health.modelLoaded` is what spark-1 itself said when it was probed before prompt
   * 1 and stored on the session row — which is the reason it is stored: a session recorded during an
   * outage stays legible afterwards as an outage rather than as an unexplained quality outlier.
   */
  const health = handed.health ?? session?.health ?? null;
  const noGpu = app?.ttsConfigured === false || health?.modelLoaded === false;

  // ── the one POST, and then the polling ────────────────────────────────────

  /**
   * `complete` is fired exactly once, and the guard is a ref rather than state.
   *
   * StrictMode double-invokes effects in development, and `POST /complete` is the route that derives
   * the seed, provisions and either reveals or defers. Firing it twice is two jobs on the one
   * serialized GPU (VOX-LAW 30) for one woman's session — on a box shared with audiobook's narration
   * queue.
   */
  const completed = useRef(false);

  useEffect(() => {
    if (sessionId === null) return;
    const ac = new AbortController();
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    // The URL keeps whatever the person arrived with. A claim link that navigates on to the payoff
    // as a `ses_` id would be a link that stops working the moment the cookie does — which is the
    // one thing VOX-LAW 20 exists to prevent.
    const onward = encodeURIComponent(handle);

    const tick = async (): Promise<void> => {
      if (stopped) return;
      try {
        const view = await api.sessionView(sessionId, ac.signal, claimToken);
        if (stopped) return;
        setSession(view);
        if (view.state === "revealed") {
          // §3.13's three screens: make a voice, wait, hear it. `replace`, so Back from the payoff
          // does not return her to a countdown for something she has already heard.
          navigate(`/s/${onward}/hear`, { replace: true });
          return;
        }
        if (view.state === "deferred" || noGpu || Date.now() - startedAt > GIVE_UP_MS) {
          setEnding("deferred");
          return;
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        // VOX-LAW 16b. A failed poll is not a sentence on her screen: it is a retry, and if it keeps
        // failing the give-up clock above lands her on the deferred ending, which is warm, designed
        // and true — her recordings really are safe, on the origin, and Karti really will send it.
        const d = describeForConsole(err);
        console.error(`wait: poll failed at ${d.where}: ${d.message}`);
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    };

    const boot = async (): Promise<void> => {
      if (!completed.current) {
        completed.current = true;
        try {
          /**
           * ⚠️ **This answers a `CompleteResponse`, not a `SessionView`.** It used to be parsed as
           * the latter, which threw on every call and sent the one answer that decides how the
           * session ends into a `console.error`. `path` is that answer, decided on the server by
           * observation — has any worker polled the lane in the last 60 s — so a build with no
           * worker at all takes §3.14's ending honestly, with nothing here to switch on.
           */
          const done = await api.completeSession(sessionId, claimToken);
          if (stopped) return;
          if (done.path === "deferred") {
            setEnding("deferred");
            return;
          }
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          const d = describeForConsole(err);
          console.error(`wait: complete failed at ${d.where}: ${d.message}`);
          // A failed `complete` does not lose anything: every recording is already on the origin,
          // and VOX-LAW 26 exempts an unclaimed session from every sweep. The honest ending is the
          // deferred one, and the claim link below is what makes it true rather than a consolation.
          setEnding("deferred");
          return;
        }
      }
      await tick();
    };

    void boot();
    return () => {
      stopped = true;
      ac.abort();
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, claimToken, handle, navigate, noGpu]);

  // ── the countdown ─────────────────────────────────────────────────────────

  /**
   * Whole seconds, drawn only while there is something to count down to. It stops at zero rather
   * than going negative, and `FAMILY.wait.almost` takes over — which is the honest thing to say when
   * a p90 budget has been exceeded and the answer is still coming.
   */
  useEffect(() => {
    if (ending !== "waiting" || noGpu) return;
    const started = Date.now();
    const id = setInterval(() => setLeftMs(Math.max(0, REVEAL_BUDGET_MS - (Date.now() - started))), 250);
    return () => clearInterval(id);
  }, [ending, noGpu]);

  // ── the durable handle ────────────────────────────────────────────────────

  /**
   * VOX-LAW 20. Fetched from the server rather than remembered from the tab that minted it, because
   * this is the most reloaded screen in the product: she photographs it, the laptop sleeps, the tab
   * restores, somebody refreshes because it "looked stuck".
   *
   * The fallback assembles the URL from the token handed over at creation and `publicOrigin` from
   * the session envelope — never from `window.location.origin`, which through the tailnet is
   * `100.92.185.76:8912` and would print a link that works from exactly one house.
   */
  useEffect(() => {
    if (sessionId === null) return;
    const ac = new AbortController();
    api.links(sessionId, ac.signal, claimToken).then(setLinks, (err: unknown) => {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const d = describeForConsole(err);
      console.error(`wait: links failed at ${d.where}: ${d.message}`);
    });
    return () => ac.abort();
  }, [sessionId, claimToken]);

  const claimUrl =
    links?.claimUrl ??
    (handed.claimToken && app?.publicOrigin
      ? `${app.publicOrigin.replace(/\/+$/, "")}/c/${handed.claimToken}`
      : null);

  /**
   * The clipboard can reject — an insecure context, a Safari gesture it did not accept — and there
   * is no string in `copy.ts` for "copying failed", deliberately: inventing one here would put a
   * sentence on the family path from outside the one file that is allowed to hold them.
   *
   * So the failure is answered with an *action* rather than a sentence: select the whole link, which
   * `.vox-token`'s `user-select: all` already makes one gesture, so ⌘C works and the person on the
   * phone can read it aloud. Nothing is said, because nothing needs to be.
   */
  const selectToken = useCallback(() => {
    const node = tokenRef.current;
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  /**
   * §2.4's "email this to yourself", via her own mail client.
   *
   * This used to `POST /sessions/:id/claim-link/email` and answer the failure with a `console.error`
   * — which meant that on the last screen of every session in this build, she typed her address,
   * pressed Send, and nothing happened anywhere she could see. There is no mail transport in this
   * product to make that request succeed (`lib/claimLink.ts` has the reason), so the send is the
   * `mailto:` that was always going to be the fallback, without the round trip in front of it.
   */
  const sendEmail = useCallback(() => {
    if (claimUrl && emailClaimLink(email, claimUrl)) setEmailed(true);
  }, [email, claimUrl]);

  // ── what she said ─────────────────────────────────────────────────────────

  /**
   * "Here's what you said." The plan is resolved at session creation, so a script bump cannot
   * re-plan a session that is halfway through, and these are the exact lines she read.
   *
   * `promptById` throws on an id this build does not know — which is possible in one real case: a
   * session created under `scriptVersion` 1 and reloaded after `script.ts` has moved. That is not
   * her problem and it is not an error screen; the unknown line is simply left out.
   */
  const said = useMemo(() => {
    const out: string[] = [];
    for (const id of session?.plan ?? []) {
      try {
        out.push(promptById(id).text);
      } catch (err) {
        if (!(err instanceof ScriptError)) throw err;
        console.error(`wait: prompt ${id} is not in this build's script.ts`);
      }
    }
    return out;
  }, [session]);

  const name = session?.subjectName ?? null;
  const helper = app?.helperName ?? null;
  const total = session?.plan.length ?? 0;
  const saved = session?.takes.length ?? 0;
  const seconds = Math.ceil(leftMs / 1000);

  /**
   * ⚠️ **`notFound` and a null session are DIFFERENT STATES and this page used to merge them.**
   *
   * `lib/handle.ts` forbids the merge in writing — *"Null is 'still resolving'; `notFound` is the
   * server's answer"* — and this file had no `notFound` branch at all, so an unresolvable handle fell
   * through to `<Blank />` and stayed there. Measured on the production-shaped server:
   * `/s/:sessionId/wait` rendered **one single colour across the whole 1280×860 screenshot**, no
   * text, no control, for any browser without the session cookie. The same for `/s/:sessionId/hear`.
   * A permanently blank page is the one outcome with no next step in it, on the two routes a family
   * is most likely to reopen from a bookmark.
   *
   * The designed screen for exactly this is the one `Claim.tsx` already renders: the warm title, the
   * not-ready notice, and the retention promise. Nothing here has to be invented — and inventing
   * would be forbidden anyway (§3.2).
   */
  if (notFound) {
    return (
      <Stage>
        <h1 className="vox-say">{FAMILY.back.title}</h1>
        <Notice>{helper ? say(FAMILY.back.notReady, { helper }) : FAMILY.mishap.keepGoing}</Notice>
        <p className="vox-quiet">{FAMILY.keep.retention}</p>
      </Stage>
    );
  }

  // Nothing renders until the name is known: every sentence on this screen has `{name}` or
  // `{helper}` in it, and `say()`'s fallback is a warm sentence about a recording — not a title.
  if (!name) return <Blank />;

  return (
    <Stage top>
      <h1 className="vox-say">{say(FAMILY.wait.title, { name })}</h1>

      {ending === "deferred" ? (
        <>
          {/*
            §3.14's third point, in the order it is written there: the recordings are safe, the
            helper will send it tonight, and the durable link is right here. Not an apology, not an
            explanation of what a GPU is, and no mention of anything being asleep — that sentence
            (`FAMILY.deferred.upFront`) belongs to the studio, BEFORE prompt 1, where it was a choice
            she made. Repeating it now would turn her choice back into a discovery.
          */}
          <Notice>{helper ? say(FAMILY.deferred.after, { helper }) : FAMILY.mishap.keepGoing}</Notice>
          <Announce>{helper ? say(FAMILY.deferred.after, { helper }) : ""}</Announce>
        </>
      ) : (
        <p className="vox-lead">
          {seconds > 0 ? say(FAMILY.wait.seconds, { seconds }) : FAMILY.wait.almost}
        </p>
      )}

      {/* The honest count, small and quiet, and only while the drain is behind. §3.12. */}
      {total > 0 && saved < total && (
        <p className="vox-quiet">{say(FAMILY.progress.saved, { n: saved, total })}</p>
      )}

      {/* Something of her own to look at. Absent rather than faked when the studio handed nothing
          over — a grey box that says "waveform" is worse than no waveform. */}
      {handed.peaks && handed.peaks.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {handed.peaks.map((peaks, i) => (
            <Waveform key={i} peaks={peaks} label={say(FAMILY.wait.title, { name })} />
          ))}
        </div>
      )}

      {said.length > 0 && (
        <section>
          <h2 className="vox-lead">{FAMILY.wait.body}</h2>
          <ul className="vox-said" style={{ marginTop: "var(--space-4)" }}>
            {said.map((line, i) => (
              <li key={i} className="vox-said-line">
                {line}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        ════════════════════════════════════════════════════════════════════════
        THE DURABLE HANDLE — §2.4, VOX-LAW 20
        ════════════════════════════════════════════════════════════════════════
        "At session completion the server mints a 24-byte token, shows the URL on screen in large
        type with a copy button and an 'email this to yourself' field."

        It is on the deferred ending and not only on the payoff, and that is the whole point of the
        law: on the day the GPU is down, this link is the *only* thing standing between a family and
        a recording they cannot make again. A cleared cookie, a private window, a handed-back phone,
        a different browser — the recording is still reachable.

        `retention` is stated once, in plain words, with no number that will rot: "This stays here
        until someone saves it. Nothing deletes it." VOX-LAW 26 is what makes that sentence true —
        a sweep "may never touch an unclaimed session at all."
      */}
      {ending === "deferred" && claimUrl && (
        <section className="vox-card" aria-labelledby="claim-title" style={{ marginTop: "var(--space-4)" }}>
          <h2 id="claim-title" className="vox-lead">
            {FAMILY.keep.linkTitle}
          </h2>
          <p className="vox-quiet" style={{ marginTop: "var(--space-2)" }}>
            {FAMILY.keep.linkBody}
          </p>

          <p className="vox-token" ref={tokenRef} style={{ marginTop: "var(--space-4)" }}>
            {claimUrl}
          </p>

          <div className="vox-copyrow" style={{ marginTop: "var(--space-4)" }}>
            <CopyButton
              value={claimUrl}
              label={FAMILY.keep.linkCopy}
              copiedLabel={FAMILY.keep.linkCopied}
              onFail={selectToken}
            />
          </div>

          <div style={{ marginTop: "var(--space-5)" }}>
            <Field
              label={FAMILY.keep.linkEmailLabel}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <div className="vox-btn-row" style={{ marginTop: "var(--space-3)" }}>
              <Button variant="plain" onClick={sendEmail}>
                {emailed ? FAMILY.keep.linkEmailSent : FAMILY.keep.linkEmailButton}
              </Button>
            </div>
          </div>

          <p className="vox-quiet" style={{ marginTop: "var(--space-5)" }}>
            {FAMILY.keep.retention}
          </p>
        </section>
      )}
    </Stage>
  );
}
